import * as React from "react";
import { JSX } from "react";
import { useHistory } from "react-router-dom";
import { toast, ToastOptions } from "react-toastify";
import { ClientService } from "@app/client/clientService";
import { Client } from "@app/client/client";
import { JobType, JobQueue, JobQueueService } from "@app/jobs";
import TestJobQueueMappingsFlyout from "@app/jobs/job-queue-mapping/TestJobQueueMapFlyout";
import { JobQueueMapListItem, JobQueueMapSummary } from "@app/jobs/job-queue-mapping/jobQueueMapSummary";
import { FlyoutLink } from "@app/shared/Flyouts";
import FlyoutContextProvider from "@app/shared/Flyouts/FlyoutContext";
import { Modal } from "@app/shared/Modals";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ColumnConfig, wafflesKeyColumnName, SortDirections, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { SortingMetadata } from "@app/shared/waffles/exports";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { MultiselectItem, MultiselectOnChangeResponse, QESMultiSelect } from "@app/shared/qes-standards/forms/Multiselect";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import SingleSelectNestedDropdown from "@app/shared/checkbox-dropdown/SingleSelectNestedDropdown";
import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import GeographyService from "@app/geography/geographyService";
import { CountyLite } from "@app/geography/county";
import JobQueueMapFlyout from "@app/jobs/job-queue-mapping/JobQueueMapFlyout";
import { EnumerationItemModel } from "@app/shared/interfaces/enumerationItemModel";


interface JobQueueMapTableData {
  [wafflesKeyColumnName]: string;
  clientId?: number;
  clientName: string;
  jobQueueId?: number;
  jobQueueMapId: JSX.Element;
  jobQueueName: string;
  jobTypeId?: number;
  jobTypeName: string;
  projectId?: number;
  projectName: string;
  countySSA: string;
}

interface JobQueueMapFilter {
  jobTypes: string[];
  jobQueue: string;
  jobQueueId: number;
  client: string;
  project: string;
  projectId: number;
  countySSA: string;
  clientId: number;
}

const jobQueueMapsColumnConfig: ColumnConfig = {
  clientId: {
    displayText: "Client ID",
    sortOrder: 50,
    testId: "clientId",
  },
  clientName: {
    displayText: "Client",
    sortOrder: 60,
    testId: "clientName",
  },
  countySSA: {
    displayText: "County SSA",
    sortOrder: 90,
    testId: "countySSA",
  },
  jobQueueId: {
    displayText: "Job Queue ID",
    sortOrder: 100,
    testId: "jobQueueId",
  },
  jobQueueMapId: {
    displayText: "Id",
    noSort: true,
    sortOrder: 10,
    testId: "jobQueueMapId",
  },
  jobQueueName: {
    displayText: "Job Queue",
    sortOrder: 40,
    testId: "jobQueueName",
  },
  jobStatus: {
    displayText: "Job Status",
    sortOrder: 90,
    testId: "jobStatus",
  },
  jobStatusDate: {
    displayText: "Job Status Date",
    sizeContentToFit: true,
    sortOrder: 100,
    testId: "jobStatusDate",
  },
  jobTypeId: {
    displayText: "Job Type ID",
    sortOrder: 20,
    testId: "jobTypeId",
  },
  jobTypeName: {
    displayText: "Job Type",
    sortOrder: 30,
    testId: "jobTypeName",
  },
  projectId: {
    displayText: "Project ID",
    sortOrder: 70,
    testId: "projectId",
  },
  projectName: {
    displayText: "Project",
    sortOrder: 80,
    testId: "projectName",
  },
};

interface PageSettings {
  skip: number,
  sortDetails: SortingMetadata<JobQueueMapTableData>,
}

interface JobTypeMultiselectItem extends MultiselectItem {
  value: JobType
}

const jobTypesMap: { [key: string]: JobType } = Object.fromEntries(
  Object.entries(JobType)
);

const WrappedJobQueueMap: React.FC<React.PropsWithChildren<unknown>> = () => {
  const clientService = React.useRef(new ClientService()).current;
  const jobQueueService = React.useRef(new JobQueueService()).current;
  const geographyService = React.useRef(new GeographyService()).current;
  const urlParams = new URLSearchParams(window.location.search);
  const history = useHistory();
  const deleteButtonRef = React.useRef<HTMLButtonElement>(null);
  const [allClients, setAllClients] = React.useState<Client[]>([]);
  const [allQueues, setAllQueues] = React.useState<JobQueue[]>([]);
  const [client, setClient] = React.useState(urlParams.get("client") ?? "");
  const [clientId, setClientId] = React.useState<number>(parseInt(urlParams.get("clientId")) || null);
  const [isFiltered, setIsFiltered] = React.useState(false);
  const [jobQueue, setJobQueue] = React.useState(urlParams.get("queue") ?? "");
  const [jobQueueId, setJobQueueId] = React.useState<number>(parseInt(urlParams.get("jobQueueId")) || null);
  const [projectId, setProjectId] = React.useState<number>(parseInt(urlParams.get("projId")) || null);
  const [jobQueueMapFilter, setJobQueueMapFilter] = React.useState<JobQueueMapFilter>(() => {
    const typesStr = urlParams.get("types");
    let initialValues: string[] = Object.values(JobType) as string[];

    if (typesStr) {
      try {
        const parsed: unknown = JSON.parse(typesStr);
        if (Array.isArray(parsed)) {
          const allValues = Object.values(JobType) as string[];
          initialValues = parsed
            .filter((v): v is string => typeof v === "string" && allValues.includes(v));
        }
      } catch { }
    }

    return {
      client: urlParams.get("client") ?? "",
      clientId: parseInt(urlParams.get("clientId"), 10) || null,
      countySSA: urlParams.get("countySSA") ?? "",
      jobQueue: urlParams.get("queue") ?? "",
      jobQueueId: parseInt(urlParams.get("jobQueueId"), 10) || null,
      jobTypes: initialValues,
      project: urlParams.get("proj") ?? "",
      projectId: parseInt(urlParams.get("projId"), 10) || null,
    };
  });
  const [jobQueueMapSummary, setJobQueueMapSummary] = React.useState<JobQueueMapSummary>(null);

  const [selectedJobTypeItems, setSelectedJobTypeItems] = React.useState<string[]>(() => {
    try {
      const typesStr = urlParams.get("types");
      return typesStr ? JSON.parse(typesStr) : Object.values(JobType) as string[];
    } catch {
      return Object.values(JobType) as string[];
    }
  });

  const [jobTypeItems, setJobTypeItems] = React.useState<EnumerationItemModel[]>([]);

  const validJobTypeValues = React.useMemo<Set<string> | null>(
    () => jobTypeItems.length > 0 ? new Set<string>(jobTypeItems.map(i => i.value)) : null,
    [jobTypeItems]
  );

  const jobTypeMultiSelectItems = React.useMemo<{ [key: string]: JobTypeMultiselectItem }>(() => {
    const items: { [key: string]: JobTypeMultiselectItem } = {};

    const sortedValues = (Object.values(JobType) as string[])
      .filter(v => !validJobTypeValues || validJobTypeValues.has(v))
      .sort((a, b) => a.localeCompare(b));

    sortedValues.forEach((displayValue) => {
      const currentState = selectedJobTypeItems.includes(displayValue)
        ? CheckboxStateEnum.Checked
        : CheckboxStateEnum.Unchecked;

      items[displayValue] = {
        currentState,
        displayValue,
        value: displayValue as JobType,
      };
    });

    return items;
  }, [selectedJobTypeItems, validJobTypeValues]);

  const [loading, setLoading] = React.useState(true);
  const [modalWarningVisible, setModalWarningVisible] = React.useState<boolean>(false);
  const [numDispRecLowerBound, setNumDispRecLowerBound] = React.useState(0);
  const [numDispRecUpperBound, setNumDispRecUpperBound] = React.useState(0);
  const [pageSettings, setPageSettings] = React.useState<PageSettings>(() => {
    const pageSetStr = urlParams.get("pageSet");
    if (pageSetStr) {
      try {
        return JSON.parse(pageSetStr) as PageSettings;
      } catch { }
    }
    return {
      skip: 0,
      sortDetails: {
        sortDirection: SortDirections.None,
        sortedColumn: null,
      },
    };
  });
  const [project, setProject] = React.useState(urlParams.get("proj") ?? "");
  const [counties, setCounties] = React.useState<CountyLite[]>([]);
  const [countySSA, setCountySSA] = React.useState(urlParams.get("countySSA") ?? "");
  const [selectedJobQueueMaps, setSelectedJobQueueMaps] = React.useState<string[]>([]);
  const take = 10;
  const [totalRecords, setTotalRecords] = React.useState(0);
  const [refreshCounter, setRefreshCounter] = React.useState(0);

  const [selectedCountyItem, setSelectedCountyItem] = React.useState<DropdownItem<string> | null>(null);


  const countyNestedItems = React.useMemo<NestingParentDropdownItem[]>(() => {
    const statesMap = new Map<string, CountyLite[]>();

    counties.forEach((c) => {
      if (!statesMap.has(c.state)) statesMap.set(c.state, []);
      statesMap.get(c.state)!.push(c);
    });

    return Array.from(statesMap.entries())
      .map(([state, cs]) => ({
        checkboxStatus: CheckboxStateEnum.Unchecked,  
        children: cs
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((c) => ({
            key: c.countySSA,
            name: `${c.countySSA} – ${c.name}`,  
          })),
        key: state,
        name: state,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [counties]);

  const handleSearch = () => {
    const items: Array<JobTypeMultiselectItem> = [];
    Object.values(jobTypeMultiSelectItems).forEach(v => items.push(v));

    setJobQueueMapFilter({
      client,
      clientId,
      countySSA,
      jobQueue,
      jobQueueId,
      jobTypes: items.filter(i => selectedJobTypeItems.includes(i.value)).map(i => i.value),
      project,
      projectId,
    });
   
    setPageSettings(prev => ({ ...prev, skip: 0 }));
    setRefreshCounter(prev => prev + 1);
  };

  const clearFilter = () => {
    const allValues = validJobTypeValues
      ? (Object.values(JobType) as string[]).filter(v => validJobTypeValues.has(v))
      : Object.values(JobType) as string[];

    setSelectedJobTypeItems(allValues);
    setJobQueue("");
    setJobQueueId(null);
    setClient("");
    setClientId(null);
    setProject("");
    setProjectId(null);
    setCountySSA("");
    setSelectedCountyItem(null);

    setJobQueueMapFilter({
      client: "",
      clientId: null,
      countySSA: "",
      jobQueue: "",
      jobQueueId: null,
      jobTypes: allValues,
      project: "",
      projectId: null,
    });

    setIsFiltered(false);
    setPageSettings(prev => ({ ...prev, skip: 0 }));
    setRefreshCounter(prev => prev + 1);
  };

  const handleCheckboxChange = ((selectedKeys: string[]) => {
    setSelectedJobQueueMaps(selectedKeys);
  });

  const handleDeleteMapsClick = () => {
    setModalWarningVisible(true);
  }

  const handleSort = (data: JobQueueMapTableData[], newSortedColumn: keyof JobQueueMapTableData, direction: SortDirections) => {
    setPageSettings({
      ...pageSettings,
      skip: 0,
      sortDetails: {
        sortDirection: direction,
        sortedColumn: newSortedColumn,
      }
    });
    return data;
  }

  const handleSearchKeyUp = (e: React.KeyboardEvent) => {
    if (e.key.toLowerCase() === "enter") {
      handleSearch();
    }
  }

  const onClearAllJobTypes = () => setSelectedJobTypeItems([]);

  const onDeleteConfirm = async () => {
    const toastOptions: ToastOptions = {
      closeOnClick: true,
      containerId: "z-index-9999",
    };

    try {
      await jobQueueService.deleteJobQueueMaps(selectedJobQueueMaps);
      setSelectedJobQueueMaps([]);
      handleSearch();
      toast.success('Job Queue Map(s) Deleted', toastOptions);
    }
    catch {
      toast.error('Delete Job Queue Map Failed', toastOptions);
    }
    setModalWarningVisible(false);
  }

  const onInvertJobTypes = () => {
    const selectedItems: string[] = [];
    Object.keys(jobTypeMultiSelectItems).forEach((key) => {
      if (!selectedJobTypeItems.includes(key)) {
        selectedItems.push(key);
      }
    });
    setSelectedJobTypeItems(selectedItems);
    setJobQueueMapFilter({
      ...jobQueueMapFilter,
      jobTypes: selectedItems
    });
  }

  const onSelectAllJobTypes = () => {
    const selectedItems: string[] = Object.keys(jobTypeMultiSelectItems);
    setSelectedJobTypeItems(selectedItems);
    setJobQueueMapFilter({
      ...jobQueueMapFilter,
      jobTypes: selectedItems
    });
  }

  const updateSelectedJobTypes = (response: MultiselectOnChangeResponse) => {
    const selectedItems: string[] = [];
    Object.keys(response.options).filter((key) => response.options[key].currentState === 3).forEach((key) => {
      selectedItems.push(key);
    });
    setSelectedJobTypeItems(selectedItems);
    setJobQueueMapFilter({
      ...jobQueueMapFilter,
      jobTypes: selectedItems
    });
  };

  React.useEffect(() => {
    if (jobTypeItems.length === 0) return;
    refreshData();
  }, [pageSettings, refreshCounter, jobTypeItems]);

  React.useEffect(() => {
    clientService.getAllClients().then(setClients);
    jobQueueService.getAvailableJobQueues().then(setQueues);
    geographyService.getCountiesLite(false).then(setCounties);
    jobQueueService.getJobTypeEnumerationItems().then((items) => {
      setJobTypeItems(items);
      const validValues = new Set<string>(items.map(i => i.value));
      setSelectedJobTypeItems(prev => prev.filter(v => validValues.has(v)));
      setJobQueueMapFilter(prev => ({
        ...prev,
        jobTypes: prev.jobTypes?.filter(v => validValues.has(v)) ?? prev.jobTypes,
      }));
    }).catch(() => {
      // If job types fail to load, fall back to all TypeScript values so the page still loads
      const allItems = Object.values(JobType).map(v => ({ description: v, id: 0, key: v, value: v }));
      setJobTypeItems(allItems);
    });
  }, []);

  React.useEffect(() => {
    if (countySSA && !selectedCountyItem) {  
      const found = countyNestedItems
        .flatMap(p => p.children)
        .find(c => String(c.key) === String(countySSA));

      if (found) {
        setSelectedCountyItem(found);
      } 
    }
  }, [countySSA, countyNestedItems]);

  const jobQueueMapTableData = React.useMemo<JobQueueMapTableData[]>(() => {
    let data: JobQueueMapTableData[] = [];

    if (jobQueueMapSummary) {
      data = jobQueueMapSummary.jobQueueMaps.map((jobQueueMap: JobQueueMapListItem) => ({
        clientId: jobQueueMap.clientId,
        clientName: jobQueueMap.clientName,
        countySSA: jobQueueMap.countySSA,
        jobQueueId: jobQueueMap.jobQueueId,
        jobQueueMapId: (
          <FlyoutLink
            content={{
              body: (
                <JobQueueMapFlyout
                  clients={allClients}
                  jobQueues={allQueues}
                  jobTypeItems={jobTypeItems}
                  counties={counties}
                  countyNestedItems={countyNestedItems}
                  onUpsert={async (request) => {
                    await jobQueueService.upsertJobQueueMap(request);
                    toast.success(
                      request.jobQueueMapId ? "Job Queue Map Updated" : "Job Queue Map Created",
                      { closeOnClick: true, containerId: "z-index-9999" }
                    );
                    refreshData();
                  }}
                  mode="edit"
                  initialData={jobQueueMap}
                />
              ),
              title: `Edit Job Queue Map #${jobQueueMap.jobQueueMapId}`,
            }}
            testId={`edit-job-queue-map-${jobQueueMap.jobQueueMapId}`}
            classNames="qes-button-secondary"
          >
            {jobQueueMap.jobQueueMapId}
          </FlyoutLink>
        ),
        jobQueueName: jobQueueMap.jobQueueName ? jobQueueMap.jobQueueName : "--",
        jobTypeId: jobQueueMap.jobTypeId,
        jobTypeName: jobQueueMap.jobTypeName,
        key: jobQueueMap.jobQueueMapId.toString(),
        projectId: jobQueueMap.projectId,
        projectName: jobQueueMap.projectName,
      }));
    }
    return data;
  }, [jobQueueMapSummary, allClients, allQueues, countyNestedItems]);

  React.useEffect(() => {
    setLoading(false);
  }, [jobQueueMapSummary]);

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => setPageSettings({ ...pageSettings, skip: (pageNumber - 1) * take }),
    recordsPerPage: take,
    skip: pageSettings.skip,
    totalRecords: jobQueueMapSummary?.totalJobQueueMaps ?? 0,
  };

  const setClients = (clients: Client[]) => setAllClients(clients);
  const setQueues = (jobQueues: JobQueue[]) => setAllQueues(jobQueues);

  const refreshData = () => {
    setLoading(true);

    const filteredTypes =
      jobQueueMapFilter.jobTypes && Array.isArray(jobQueueMapFilter.jobTypes)
        ? jobQueueMapFilter.jobTypes
        : Object.values(JobType) as string[];

    jobQueueService
      .getJobQueueMapSummary(
        pageSettings.skip,
        take,
        filteredTypes,
        pageSettings.sortDetails.sortedColumn,
        pageSettings.sortDetails.sortDirection,
        jobQueueMapFilter.jobQueue,
        jobQueueMapFilter.jobQueueId,
        jobQueueMapFilter.client,
        jobQueueMapFilter.clientId,
        jobQueueMapFilter.project,
        jobQueueMapFilter.projectId,
        jobQueueMapFilter.countySSA
      )
      .then(summary => {
        setJobQueueMapSummary(summary);
        setTotalRecords(summary.totalJobQueueMaps);

        setNumDispRecLowerBound(pageSettings.skip + 1);
        setNumDispRecUpperBound(summary.jobQueueMaps.length + pageSettings.skip);

        setIsFiltered(
          !!(
            (jobQueueMapFilter.jobTypes && jobQueueMapFilter.jobTypes.length < (validJobTypeValues?.size ?? Object.values(JobType).length)) ||
            jobQueueMapFilter.jobQueue ||
            jobQueueMapFilter.jobQueueId ||
            jobQueueMapFilter.client ||
            jobQueueMapFilter.clientId ||
            jobQueueMapFilter.project ||
            jobQueueMapFilter.projectId ||
            jobQueueMapFilter.countySSA
          )
        );
       
        const searchParams = new URLSearchParams(window.location.search);
        if (pageSettings.skip > 0 || pageSettings.sortDetails.sortDirection !== SortDirections.None) {
          searchParams.set("pageSet", JSON.stringify(pageSettings));
        } else {
          searchParams.delete("pageSet");
        }
        if (jobQueueMapFilter.jobTypes && jobQueueMapFilter.jobTypes.length < (validJobTypeValues?.size ?? Object.values(JobType).length)) {
          searchParams.set("types", JSON.stringify(jobQueueMapFilter.jobTypes));
        } else {
          searchParams.delete("types");
        }
        if (jobQueueMapFilter.jobQueue) {
          searchParams.set("queue", jobQueueMapFilter.jobQueue);
        } else {
          searchParams.delete("queue");
        }
        if (jobQueueMapFilter.jobQueueId) {
          searchParams.set("jobQueueId", jobQueueMapFilter.jobQueueId.toString());
        } else {
          searchParams.delete("jobQueueId");
        }
        if (jobQueueMapFilter.client) {
          searchParams.set("client", jobQueueMapFilter.client);
        } else {
          searchParams.delete("client");
        }
        if (jobQueueMapFilter.project) {
          searchParams.set("proj", jobQueueMapFilter.project);
        } else {
          searchParams.delete("proj");
        }
        if (jobQueueMapFilter.projectId) {
          searchParams.set("projId", jobQueueMapFilter.projectId.toString());
        } else {
          searchParams.delete("projId");
        }
        if (jobQueueMapFilter.countySSA) {
          searchParams.set("countySSA", jobQueueMapFilter.countySSA);
        } else {
          searchParams.delete("countySSA");
        }
        if (jobQueueMapFilter.clientId) {
          searchParams.set("clientId", jobQueueMapFilter.clientId.toString());
        } else {
          searchParams.delete("clientId");
        }

        history.replace(history.location.pathname + "?" + searchParams.toString());
      })
      .catch(() => setJobQueueMapSummary(null));
  };

  const bulkActionBar = (
    <span>
      <QESButton
        disabled={selectedJobQueueMaps.length === 0}
        onClick={handleDeleteMapsClick}
        qesTestId="job-queue-map__delete-maps"
        qesType="secondary"
        qesRef={deleteButtonRef}
      >
        <QesIcon iconName={QesIcons.TrashV1} description="Delete" className="align-text-top" /> Delete Maps
      </QESButton>
    </span>
  );

  return (
    <Page documentTitle="Job Queue Map - Operations">
      <Breadcrumbs template={BreadcrumbTemplateEnum.Operations} items={["Job Queue Map"]} />
      <PageInner>
        <PageHeader title="Job Queue Map Listing">
          <PageHeaderRight>
            <FlyoutLink
              content={{
                body: (
                  <JobQueueMapFlyout
                    clients={allClients}
                    jobQueues={allQueues}
                    jobTypeItems={jobTypeItems}
                    counties={counties}
                    countyNestedItems={countyNestedItems}
                    onUpsert={async (request) => {
                      await jobQueueService.upsertJobQueueMap(request);
                      toast.success(request.jobQueueMapId ? "Job Queue Map Updated" : "Job Queue Map Created", {
                        closeOnClick: true,
                        containerId: "z-index-9999",
                      });
                      handleSearch();
                    }}
                  />
                ),
                title: "Add Job Queue Map Entry",
              }}
              testId="job-queue-map-filters__add"
              classNames="qes-button-secondary"
            >
              <QesIcon iconName={QesIcons.Plus} description="Add" className="align-text-top" /> Add New Mapping
            </FlyoutLink>
            <FlyoutLink
              content={{
                body: (
                  <TestJobQueueMappingsFlyout
                    clients={allClients}
                    jobTypes={jobTypesMap}
                    countyItems={countyNestedItems}
                  />
                ),
                title: "Test Job Queue Mapping",
              }}
              testId="job-queue-map-filters__test"
              classNames="qes-button-secondary"
            >
              <QesIcon iconName={QesIcons.Check} description="Test" className="align-text-top" /> Test Mappings
            </FlyoutLink>
          </PageHeaderRight>
        </PageHeader>
        <PageSection>
          <div className="job-queue-map-filters mb-3 mt-3 col">
            <div className="row">
              <div className="me-3 col-6">
                <QESMultiSelect
                  label="Job Types"
                  onChange={updateSelectedJobTypes}
                  options={jobTypeMultiSelectItems}
                  testId="job-queue-map-job-types"
                  containerClassName="sticky-job-types-multiselect"
                />
                <QESButton
                  qesTestId="job-queue_map_filters__defautuserroles-SelectAll"
                  qesType="secondary"
                  onClick={onSelectAllJobTypes}>
                  Select All</QESButton>
                <QESButton
                  qesTestId="job-queue-map-filters__defautuserroles-ClearAll"
                  qesType="secondary"
                  onClick={onClearAllJobTypes}>
                  Clear All</QESButton>
                <QESButton
                  qesTestId="job-queue-map-filters__defautuserroles-Invert"
                  qesType="secondary"
                  onClick={onInvertJobTypes}>
                  Invert Selection</QESButton>
              </div>
              <div className="col">
                <QESTextInput label="Job Queue" testId="job-queue-map-filters__job-queue" value={jobQueue} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setJobQueue(e.target.value)} />
                <QESTextInput label="Job Queue ID" testId="job-queue-map-filters__jobQueueId" value={jobQueueId !== null ? jobQueueId.toString() : ""} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => { const value = e.target.value; setJobQueueId(value ? parseInt(value) : null); }} />
                <QESTextInput label="Client" testId="job-queue-map-filters__client" value={client} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setClient(e.target.value)} />
                <QESTextInput label="Client ID" testId="job-queue-map-filters__clientId" value={clientId !== null ? clientId.toString() : ""} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => { const value = e.target.value; setClientId(value ? parseInt(value) : null); }} />
                <QESTextInput label="Project" testId="job-queue-map-filters__project" value={project} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setProject(e.target.value)} />
                <QESTextInput label="Project ID" testId="job-queue-map-filters__projectId" value={projectId !== null ? projectId.toString() : ""} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => { const value = e.target.value; setProjectId(value ? parseInt(value) : null); }} />
                <SingleSelectNestedDropdown
                  label="County"
                  testId="job-queue-map-filters__county"
                  containerClassName="col-12 p-0"
                  items={countyNestedItems}
                  selectedItem={selectedCountyItem ?? undefined}
                  customSelectInputText={selectedCountyItem?.name ?? countySSA ?? ""}
                  onSelectionChanged={(item) => {
                    setSelectedCountyItem(item);
                    const newSSA = item?.key ?? null;
                    setCountySSA(newSSA);
                    setJobQueueMapFilter(prev => ({ ...prev, countySSA: newSSA }));
                    setPageSettings(prev => ({ ...prev, skip: 0 }));
                  }}
                />
              </div>
            </div>
            <div className="row mb-3">
              <QESButton qesType="primary" className="col-auto" qesTestId="job-queue-map-filters__search-button" onClick={handleSearch} disabled={loading}>Search</QESButton>
              <QESButton qesType="secondary" className="col-auto" qesTestId="job-queue-map-filters__clear-filters" onClick={clearFilter} disabled={!isFiltered || loading}>Clear</QESButton>
            </div>
          </div>

          <div className="mt-4">
            <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
              <p className="mb-0">
                Displaying {totalRecords > 0 ? `${numDispRecLowerBound}-${numDispRecUpperBound} of ${totalRecords}` : "0"}{" "}
                job queue maps {isFiltered ? "(filtered)" : ""}
              </p>
              {bulkActionBar}
            </div>

            <div className="table-responsive p-0" style={{ marginLeft: '-1.5rem', marginRight: '-1.5rem', width: 'calc(100% + 3rem)' }}>
              <Waffles<JobQueueMapTableData>
                bulkActionBar={null}
                columns={jobQueueMapsColumnConfig}
                customSort={handleSort}
                data={jobQueueMapTableData}
                enablePagination={true}
                handleCheckboxChange={handleCheckboxChange}
                loading={loading}
                passThroughSorting={true}
                paginationProps={paginationProps}
                selectedKeys={selectedJobQueueMaps ?? []}
                showCheckboxes={true}
                testId="jobs-queue-map__waffles-table"
                fullWidth={true}
              />
            </div>

            <Modal
              isVisible={modalWarningVisible}
              closeModal={() => setModalWarningVisible(false)}
              content={{
                body: <>{`You are about to delete ${selectedJobQueueMaps.length} job queue map entries.`}<br />Continue with delete?</>,
                cancelButtonText: "Cancel",
                confirmButtonAction: onDeleteConfirm,
                confirmButtonText: "Delete",
                confirmButtonType: "primary-danger",
                title: "Warning",
              }}
              hideBodyScroll={true}
              triggerElement={deleteButtonRef}
            />
          </div>
        </PageSection>
      </PageInner>
    </Page>
  );
}

export const JobQueueMap: React.FC<unknown> = () => {
  return (
    <FlyoutContextProvider>
      <WrappedJobQueueMap />
    </FlyoutContextProvider>
  );
}
